import type { JSX } from 'react';
import { Box, styled } from '@mui/material';
import { SquareArrowOutUpRightIcon } from 'lucide-react';
import CustomTypography from '@/components/Common/Typography';
import { corePalette } from '@/themes/colours';
import { caseStatusesMap } from '@/constants/TaskDashboard/status';
import { POPUP_GLYPH_INDENT, POPUP_TEXT_SIZE } from '@/constants/PatientDashboard/patientTimeline';
import type { IPatientTimelineReportVersion } from '@/types/PatientDashboard/PatientTimeline.types';
import type { ReportType } from '@/types/Reports/Reports.types';
import { buildReportVersionHref, formatDisplayDate } from './PatientTimeline.util';
import { ExternalLink } from './TimelinePopup.styles';
import TimelinePopupBadge from './TimelinePopupBadge';

const VersionRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'auto auto 1fr auto',
  alignItems: 'center',
  gap: '8px',
  paddingLeft: POPUP_GLYPH_INDENT,
});

const versionTextSx = {
  fontSize: POPUP_TEXT_SIZE,
  lineHeight: '16px',
} as const;

interface IProps {
  patientId?: string;
  type: ReportType;
  version: IPatientTimelineReportVersion;
}

export default function ReportVersionLine({ patientId, type, version }: IProps): JSX.Element {
  // zcc_reports.status is free text, so mapReportStatus can pass a value through that
  // has no entry in the dashboard map — the version then badges its raw status.
  const chipProps = caseStatusesMap[version.caseStatus]?.chipProps;

  return (
    <VersionRow>
      <CustomTypography
        fontWeight="medium"
        sx={{ ...versionTextSx, color: corePalette.grey200 }}
      >
        {`v${version.version}`}
      </CustomTypography>
      <TimelinePopupBadge
        label={chipProps?.status ?? version.caseStatus}
        backgroundColor={chipProps?.backgroundColor}
        color={chipProps?.color}
      />
      <CustomTypography sx={{ ...versionTextSx, color: corePalette.grey100 }}>
        {formatDisplayDate(version.approvedAt)}
      </CustomTypography>
      {patientId && (
        <ExternalLink
          href={buildReportVersionHref(patientId, type, version)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <CustomTypography
            fontWeight="medium"
            sx={{ ...versionTextSx, color: corePalette.green150 }}
          >
            View
          </CustomTypography>
          <SquareArrowOutUpRightIcon aria-hidden />
        </ExternalLink>
      )}
    </VersionRow>
  );
}
